import { isMobile } from "react-device-detect"

import { aemAxiosInstance, apim } from "@/constants/api"
import {
  apimSubscriptionKey,
  apiBaseUrl,
  authorBaseUrl,
  PDP_END_POINT_SUBSCRIPTION_KEY,
  PLP_END_POINT,
  PLP_END_POINT_SUBSCRIPTION_KEY,
  EXCEPTIONAL_SITENAME,
  SSR_PLP_FL,
  searchPlpEndPoint,
} from "@/constants"
import { getPDPUrlWithConfig, getUserPersona } from "@/utils/helper"
import qs from "qs"
import {
  addCompareProducts,
  clearProduct,
  updateCompareStatus,
} from "@/store/features/compareSlice"
import { store } from "@/store"
import { getConfig } from "@/constants/config"
import Cookie from "universal-cookie"

const isServer = typeof window === "undefined"

const addCollectionParam = (url = "", brandName = "") => {
  let endPoint = url
  if (brandName?.toLowerCase() !== EXCEPTIONAL_SITENAME)
    endPoint = `${url}&collections=${brandName?.toLowerCase()}`
  return endPoint
}

const getListFromServer = async (
  profileName,
  persona,
  brandName = "",
  locale
) => {
  return new Promise(async (resolve, reject) => {
    try {
      const url = addCollectionParam(
        `${apiBaseUrl}${PLP_END_POINT_SUBSCRIPTION_KEY}?profilename=${profileName}&persona=${persona}`,
        brandName
      )
      aemAxiosInstance.defaults.headers.common[
        "Ocp-Apim-Subscription-Key"
      ] = `${apimSubscriptionKey}`
      const { data: content } = await aemAxiosInstance({
        url,
        params: {
          fq: `language_s:("${locale}")`,
          fl: SSR_PLP_FL.join(","),
        },
      })
      resolve(content)
    } catch (error) {
      reject(error)
    }
  })
}

const getListFromClient = async (
  profileName,
  persona,
  accessToken,
  brandName = ""
) => {
  return new Promise(async (resolve, reject) => {
    try {
      const url = addCollectionParam(
        `${authorBaseUrl}${PLP_END_POINT}?profilename=${profileName}&persona=${persona}`,
        brandName
      )
      aemAxiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${accessToken}`
      const { data: content } = await aemAxiosInstance({
        url,
      })
      resolve(content)
    } catch (error) {
      reject(error)
    }
  })
}

const getPlpParams = (obj, targetKey) => {
  let result = null
  for (const key in obj) {
    if (Object.hasOwn(obj, key)) {
      if (key === targetKey) {
        return obj[key]
      }

      if (typeof obj[key] === "object" && obj[key] !== null) {
        result = getPlpParams(obj[key], targetKey)

        if (result !== null) {
          return result
        }
      }
    }
  }
  return result
}

const getSearchItems = async (
  searchKey,
  accessToken,
  displayName = "SEARCH"
) => {
  return new Promise(async (resolve, reject) => {
    try {
      aemAxiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${accessToken}`
      const { data: content } = await aemAxiosInstance({
        url: `${authorBaseUrl}${PLP_END_POINT}?displayname=${displayName}&q=${searchKey}`,
      })
      resolve(content)
    } catch (error) {
      reject(error)
    }
  })
}

const formatListingData = async (data, stripResponse) => {
  return new Promise((resolve, reject) => {
    try {
      const persona = getUserPersona()
      const payload = {
        facets: data?.facet_counts?.facet_fields,
        facetLabels: data.fusion?.facet_labels ?? [],
        grouped:
          data?.fusion?.isCollapsePLP === "true" ||
          data?.fusion?.isCollapsePLP === true,
        fusion: data.fusion,
        PlpSwatches: data?.facet_counts?.Plp_Swatches ?? [],
      }

      const priceRange = payload.grouped
        ? data?.facet_counts?.facet_ranges[
            `discountedPriceStartingAt.${persona}_d`
          ]
        : data?.facet_counts?.facet_ranges[`priceList.${persona}.finalPrice_d`]
      if (priceRange) {
        const gap = priceRange.gap
        // eslint-disable-next-line camelcase
        payload.facets.Price_Range = priceRange.counts.map(item =>
          typeof item === "string"
            ? `[${parseInt(item)} TO ${parseInt(item) + gap}]`
            : item
        )
      }

      // mark multiselect
      payload.facets = formatMultiSelectFacets(data)
      // set color swatches
      payload.colors = data.fusion?.color_swatch_labels ?? {}

      if (data.response) {
        if (stripResponse && data.response.docs.length > 3) {
          payload.data = data.response.docs.slice(0, 3)
        } else {
          payload.data = data.response.docs
        }

        payload.total = data.response.numFound
        payload.start = data.response.start
        if (
          data.fusion?.isCollapsePLP === "true" ||
          data.fusion?.isCollapsePLP === true
        ) {
          payload.grouped = true
        }
      } else {
        payload.data = data.grouped[Object.keys(data.grouped)[0]].groups.map(
          doc => doc.doclist.docs[0]
        )
        payload.total = data.grouped[Object.keys(data.grouped)[0]].groups.length
        payload.start = 0
        payload.grouped = true
      }
      resolve(payload)
      // update
      //   ? dispatch({ type: GET_MORE_PRODUCTS, payload: data.response.docs })
      //   : dispatch({ type: GET_PRODUCTS, payload })

      // dispatch({ type: STOP_LOADING, payload: "plp" })
    } catch (error) {
      reject(error)
    }
  })
}

const formatMultiSelectFacets = data => {
  const facets = Object.entries(data.facet_counts?.facet_fields ?? {}).map(
    fields => ({ [fields[0]]: fields[1] })
  )
  let facetField = data?.fusion["facet.field"]
  if (facetField) {
    if (typeof facetField === "string") facetField = [facetField]
    facetField.forEach(item => {
      facets.forEach((filter, index) => {
        if (filter[item]) {
          const obj = {
            [`**${item}`]: filter[item],
          }
          facets.splice(index, 1, obj)
        }
      })
    })
  }
  const result = Object.assign({}, ...facets)
  return result
}

const getRowsInitialValue = () => {
  let rowsInitialValue = 30
  if (!isServer) {
    rowsInitialValue = isMobile
      ? 30
      : sessionStorage.rows &&
        JSON.parse(sessionStorage.rows)[window.location.pathname]
      ? JSON.parse(sessionStorage.rows)[window.location.pathname]
      : 30
  }
  return rowsInitialValue
}

const addToCompareProduct = async (
  sku = "",
  data = {},
  isBackOrder = false,
  slug
) => {
  try {
    store.dispatch(updateCompareStatus(true))

    const isBackOrderable =
      (isBackOrder ||
        data?.RegionIsBackOrderable_b ||
        data?.regionIsBackOrderable) ??
      false
    const category =
      data?.Product_Category ||
      data?.ProductLocalCategory_s ||
      data.category ||
      ""

    const state = store.getState()
    const existingCompareSkus =
      state.compare?.comparedProducts?.map(p => p.compareSku || p.sku_s) || []

    const compareResult = await fetchCompareDetails(
      sku,
      existingCompareSkus,
      category
    )

    const compareData =
      compareResult?.response?.docs?.find(item => item.sku_s === sku) || {}

    const compareLabels = compareResult?.fusion?.facet_labels

    const productData = {
      ...data,
      compareData,
      compareLabels,
    }

    store.dispatch(
      addCompareProducts({
        productData,
        backorder: isBackOrderable,
        compareSku: sku,
        slug,
      })
    )
  } catch (error) {
    console.error("Error adding product to compare:", error)
    store.dispatch(updateCompareStatus(false))
  }
}

const fetchCompareDetails = async (sku, compareSkus, category) => {
  const {
    general: { lwAppName = "" },
    internationalization: { language = "en" },
  } = await getConfig()
  const allSkus = [...compareSkus, sku].filter(Boolean)
  const encodedCategory = encodeURIComponent(category)
  const fq = [
    allSkus.length > 1 ? `sku_s:(${allSkus.join(" OR ")})` : `sku_s:(${sku})`,
    `ProductLocalCategory_s: "${encodedCategory}"`,
    `language_s: ("${language}")`,
  ]

  const res = await apim.get(searchPlpEndPoint, {
    params: {
      q: "*:*",
      fq: fq,
      compare: true,
      collections: lwAppName,
      profilename: `profile_${lwAppName}_Search`,
    },
    paramsSerializer: params => {
      return qs.stringify(params, { arrayFormat: "repeat", encode: false })
    },
  })
  return res.data
}

export const getProductDataFromSkus = async (skus = [], category = "") => {
  try {
    const {
      general: { lwAppName = "" },
    } = await getConfig()

    if (!skus.length) return []

    const quotedSkus = skus.map(sku => `"${sku}"`).join(" ")
    const fq = `sku_s:(${quotedSkus})`

    // Fetch product data
    const { data: { response: { docs: productDocs = [] } = {} } = {} } =
      await apim.get(searchPlpEndPoint, {
        params: {
          fl: SSR_PLP_FL.join(","),
          q: "*:*",
          fq,
          collections: lwAppName,
          profilename: `profile_${lwAppName}_General`,
          rows: skus.length,
        },
        paramsSerializer: params =>
          qs.stringify(params, { arrayFormat: "repeat", encode: false }),
      })

    // Fetch compare data
    const compareResponse = await fetchCompareDetails("", skus, category)
    const compareDocs = compareResponse?.response?.docs || []
    const compareLabels = compareResponse?.fusion?.facet_labels || {}

    // Helper to map compare data
    const mapProduct = product => {
      const matchingCompareData = compareDocs.find(
        c => c.sku_s === product.sku_s
      )

      return {
        ...product,
        compareData: matchingCompareData,
        compareLabels,
      }
    }

    return productDocs.map(mapProduct)
  } catch (error) {
    console.error("Error fetching product data:", error)
    return []
  }
}

const removeFromCompareProduct = sku => {
  store.dispatch(clearProduct(sku))
}

const getProductDetailsFromServer = async (
  profileName,
  collections,
  slug,
  locale
) => {
  return new Promise(async (resolve, reject) => {
    try {
      aemAxiosInstance.defaults.headers.common[
        "Ocp-Apim-Subscription-Key"
      ] = `${apimSubscriptionKey}`
      const response = await aemAxiosInstance({
        url: `${apiBaseUrl}${PDP_END_POINT_SUBSCRIPTION_KEY}?profilename=${profileName}&collections=${collections}&slug=${slug}&fq=language_s:"${locale}"`,
      })
      const { data: { response: { docs = [] } = {} } = {} } = response
      const { data: { fusion: { masterVariantDataResponse = [] } = {} } = {} } =
        response
      resolve(docs[0] ? { ...docs[0], ...{ masterVariantDataResponse } } : {})
    } catch (error) {
      reject(error)
    }
  })
}

/**
 * Calculates the position index for promo banners or elements in a product grid layout.
 * @param {number} width - The current screen width.
 * @param {number} rowCols - The number of columns in the grid row.
 * @param {number} row - The current row number (1-based).
 * @param {Object} colVal - An object mapping promo layout types to column counts.
 * @param {string} promoLayout - The current promo layout type (e.g., "one-column", "two-column").
 * @return {number} The calculated position index for the promo element.
 */
const getPromoColumns = width => {
  if (width < 640) {
    return {
      "one-column": "promo-col-1",
      "two-column": "promo-col-1",
      "three-column": "promo-col-1",
    }
  } else if (width <= 1023) {
    return {
      "one-column": "promo-col-1",
      "two-column": "promo-col-2",
      "three-column": "promo-col-2",
    }
  } else {
    return {
      "one-column": "promo-col-1",
      "two-column": "promo-col-2",
      "three-column": "promo-col-3",
    }
  }
}
export const getPromoType = type => {
  if (type.includes("1")) {
    return "1-column"
  } else if (type.includes("2")) {
    return "2-column"
  } else if (type.includes("3")) {
    return "3-column"
  }
}

/**
 * Redirects the user to the Product Detail Page (PDP) and manages PLP state in session storage.
 * Updates the browser history and navigates to the PDP URL with the given SKU.
 * @param {Event} e - The event object from the click or navigation action.
 * @param {string} category - The product category.
 * @param {string} slug - The product slug (URL-friendly identifier).
 * @param {string} sku - The product SKU (Stock Keeping Unit).
 * @param {string} plpServletPath - The PLP servlet path for URL construction.
 * @param {Object} general - General configuration settings.
 * @param {Object} router - The router object for navigation (optional, may be used for client-side routing).
 * @return {void}
 */
const redirectToPdp = (
  e,
  category,
  slug,
  sku,
  plpServletPath,
  general,
  router
) => {
  if (typeof e === "object") {
    e.preventDefault()
    e.stopPropagation()
  }

  if (!category || !slug) {
    return
  }
  try {
    const url =
      plpServletPath.length > 0
        ? getPDPUrlWithConfig(category, slug, plpServletPath, general)
        : ""
    if (url) {
      router.push(`${url}?skuId=${sku}`)
    } else {
      console.error("Invalid URL generated for PDP redirection")
    }
  } catch (error) {
    console.error("Error in redirectToPdp:", error)
  }
}

/**
 * Returns the promo column layout mapping based on the current screen width.
 * @param {number} width - The current screen width.
 * @return {Object} An object mapping layout types ("one-column", "two-column", "three-column") to their promo column class names.
 */

/**
 * Determines if content should be shown based on device display settings
 * @param {string} isHideOnDesktop - "true" if content should be hidden on desktop
 * @param {string} isHideOnMobile - "true" if content should be hidden on mobile
 * @return {boolean} Whether the content should be displayed on current device
 */
const getIsPresentOnDevice = (isHideOnDesktop, isHideOnMobile) => {
  if (isHideOnMobile === "true" && isHideOnDesktop === "true") {
    return false
  }
  let showDefaultBehaviour =
    isHideOnDesktop === undefined && isHideOnMobile === undefined
  showDefaultBehaviour =
    showDefaultBehaviour ||
    (isHideOnMobile === "false" && isHideOnDesktop === "false")
  if (showDefaultBehaviour) {
    return true
  }

  // For server-side rendering, assume desktop view
  if (typeof window === "undefined") {
    return isHideOnDesktop !== "true"
  }

  const isPresentOnDeskTop = window.matchMedia("(min-width: 1280px)").matches
  if (isHideOnDesktop === "true") {
    return isPresentOnDeskTop
  }

  return isHideOnMobile === "true" ? !isPresentOnDeskTop : isPresentOnDeskTop
}
const getFilterLabel = (showFilter, staticTexts) => {
  if (showFilter) {
    return staticTexts.hideFilter
  }

  if (window.innerWidth <= 1023) {
    return staticTexts.filterSort
  }

  return staticTexts.showFilter
}

const getFilterBarProps = ({
  totalResults,
  staticTexts,
  updatedKeyWord,
  handleSearchOnchange,
  onSearch,
  typeAheadResults,
  showFilter,
  addAnalyticsForShowHideFilter,
  sorts,
  getSortItemBasedOnURL,
  optionsPreparation,
  handleSort,
  getSortOptionByValue,
  handleDatalayer,
  type,
  page,
  searchPlaceholder,
  isFilterBarSticky,
  setIsFilterBarSticky,
}) => ({
  resultsCount: `${totalResults} ${staticTexts?.results}`,
  mod: "white",

  searchInput: isFilterBarSticky
    ? {
        id: "search-result-input-sticky-scroll",
        value: updatedKeyWord,
        label: `${staticTexts?.searchBrand} ${searchPlaceholder}`,
        onChange: e => {
          handleSearchOnchange(e.target.value)
        },
        onKeyDown: e =>
          (e.key === "Enter" || e.keyCode === 13) && onSearch(updatedKeyWord),
        searchCTA: {
          text: staticTexts.searchText,
          onClick: () => {
            updatedKeyWord?.length > 0 && onSearch(updatedKeyWord)
          },
        },
        results: {
          options: updatedKeyWord?.length > 2 ? typeAheadResults : [],
          onOptionSubmit: e => {
            onSearch(e.value)
          },
        },
        clearCTA: {
          onClick: () => {
            handleSearchOnchange("")
          },
        },
      }
    : undefined,
  onStickyChange: isSticky => {
    setIsFilterBarSticky(isSticky)
  },

  filtersCTA: {
    label: getFilterLabel(showFilter, staticTexts),
    onClick: () => addAnalyticsForShowHideFilter(showFilter),
  },

  sortDropdownProps: {
    defaultValue: getSortItemBasedOnURL(staticTexts, {
      name: staticTexts.featured,
    }).query,
    options: optionsPreparation(sorts),
    className: `gbh-data-layer`,
    onSelect: e => {
      handleSort(getSortOptionByValue(sorts, e))
      handleDatalayer(getSortOptionByValue(sorts, e), {
        type,
        page,
      })
    },
    placeholder: staticTexts.sort,
  },
})

const setPLPCookie = (key, value, pathName) => {
  const cookie = new Cookie()
  const cookieExist = cookie.get(key)
  if (cookieExist && pathName) {
    if (key === "rows" || key === "scrollPosition") {
      const rowStorage = cookieExist
      rowStorage[pathName] = value
      cookie.set(key, JSON.stringify(rowStorage), { path: "/" })
    } else if (key === "plpsku") {
      cookie.update(key, value)
    } else {
      cookie.remove(key, { path: "/" })
    }
  } else {
    if (key === "rows" || key === "scrollPosition") {
      cookie.set(key, JSON.stringify({ [window.location.pathname]: value }), {
        path: "/",
      })
    } else {
      cookie.set(key, value, { path: "/" })
    }
  }
}

const scrollToSkuCard = sku => {
  if (!sku) return

  const smoothScroll = () => {
    const element = document.getElementById(sku)
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" })
    } else {
      // Keep checking until the element is rendered
      requestAnimationFrame(smoothScroll)
    }
  }

  requestAnimationFrame(smoothScroll)
}

const handleWindowScroll = plpsku => {
  if (plpsku) {
    scrollToSkuCard(plpsku)
  } else if (typeof window !== "undefined") {
    const url = new URL(window.location.href)
    if (url.searchParams.has("facets") || url.searchParams.has("sort")) {
      const wrapper =
        document.getElementsByClassName("visualNavWrapper")[0] ??
        document.getElementsByClassName("productListWrapper")[0]
      if (wrapper) {
        wrapper.scrollIntoView({ behavior: "instant" })
      }
    }
  }
}

/**
 * Returns the promo tile interval dynamically based on rowCols and promo row placement.
 * @param {number} rowCols - Number of columns in the grid.
 * @param {number} width - The current screen width.
 * @param {number} promoPlacementDesktop - Row placement for desktop.
 * @param {number} promoPlacementMobile - Row placement for mobile/tablet.
 * @returns {number} The interval after which to show the promo tile.
 */
/**
 * Returns the promo tile position index based on layout and screen width.
 * @param {number} desktopPlacement - N value for desktop.
 * @param {number} mobilePlacement - M value for mobile/tablet.
 * @param {string} layout - "one-column", "two-column", "three-column".
 * @param {number} width - Screen width.
 * @return {number} Promo tile index.
 */
export const getPromoTilePosition = (
  desktopPlacement,
  mobilePlacement,
  layout,
  width
) => {
  const desktop = parseInt(desktopPlacement)
  const mob = parseInt(mobilePlacement)
  if (width >= 1024) {
    if (layout === "one-column") return desktop * 3 - 1
    if (layout === "two-column") return desktop * 3 - 2
    if (layout === "three-column") return (desktop - 1) * 3
  } else if (width >= 640 && width <= 1023) {
    if (layout === "one-column") return mob * 2 - 1
    else if (layout === "two-column") return mob * 2 - 2
    else return mob * 2 - 2
  } else {
    // width <= 639
    return mob * 2 - 2
  }
}

export {
  getListFromServer,
  getListFromClient,
  getPlpParams,
  getSearchItems,
  formatListingData,
  getRowsInitialValue,
  addToCompareProduct,
  removeFromCompareProduct,
  getProductDetailsFromServer,
  redirectToPdp,
  getPromoColumns,
  getIsPresentOnDevice,
  addCollectionParam,
  getFilterBarProps,
  setPLPCookie,
  handleWindowScroll,
  scrollToSkuCard,
}
